import { AuthService } from './../../core/services/auth.service';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CountryService } from '../../shared/services/country.service';
import { PhoneCodeService } from '../../shared/services/phone-code.service';
import { MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { NotificationService } from './../../shared/components/notification/notification.service';
import {environment} from '../../../environments/environment';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    CommonModule,
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent {
  registrationForm: FormGroup;
  countries: any[] = [];
  phoneCodes: any[] = [];
  phonePattern = '^(\\([0-9]{3}\\)) [0-9]{3}-[0-9]{4}$';
  filteredCountries: any[] = [];

  constructor(
    private fb: FormBuilder,
    private countryService: CountryService,
    private phoneCodeService: PhoneCodeService,
    private authService: AuthService,
    private router: Router,
    private notificationService: NotificationService 
  ) {
    this.registrationForm = this.fb.group({
      fullName: [
        '',
        [Validators.required, Validators.pattern(/^[a-zA-Z\s\-']+$/)],
      ],
      email: ['', [Validators.required, Validators.email]],
      country: ['United States of America', Validators.required],
      phoneCode: ['+1', Validators.required],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern(this.phonePattern)],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(
            /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
          ),
        ],
      ],
      repeatPassword: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadCountries();
    this.loadPhoneCodes();
    this.registrationForm
      .get('country')
      ?.valueChanges.subscribe(() => this.onCountryChange());
  }

  private loadCountries(): void {
    this.countryService.getCountries().subscribe((data) => {
      this.countries = data;
      this.filteredCountries = data;
    });
  }

  private loadPhoneCodes(): void {
    this.phoneCodeService.getPhoneCodes().subscribe((data) => {
      this.phoneCodes = data.filter((code) => code.isActive=== true);
    });
  }

  onCountrySelected(event: any): void {
    const selectedCountry = this.countries.find(
      (country) => country.name === event.option.value
    );

    if (selectedCountry) {
      const selectedPhoneCode = this.phoneCodes.find(
        (phoneCode) => phoneCode.countryCode === selectedCountry.code
      )?.phoneCode;

      if (selectedPhoneCode) {
        this.registrationForm.patchValue({
          phoneCode: selectedPhoneCode,
        });
      }
    }
  }

  onCountryChange(): void {
    const value = this.registrationForm.get('country')?.value;
    if (!value) {
      this.filteredCountries = this.countries;
      return;
    }
    const filterValue = value.toLowerCase();
    this.filteredCountries = this.countries.filter((country) =>
      country.name.toLowerCase().includes(filterValue)
    );

    const selectedCountry = this.countries.find(
      (country) => country.name === value
    );

    if (selectedCountry) {
      const selectedPhoneCode = this.phoneCodes.find(
        (phoneCode) => phoneCode.countryCode === selectedCountry.code
      )?.phoneCode;

      if (selectedPhoneCode) {
        this.registrationForm.patchValue({
          phoneCode: selectedPhoneCode,
        });
      }
      this.updatePhoneValidation(selectedCountry.name);
      const currentPhoneNumber =
        this.registrationForm.get('phoneNumber')?.value;
      if (currentPhoneNumber) {
        this.formatPhoneNumber({ target: { value: currentPhoneNumber } });
      }
    }
  }

  private updatePhoneValidation(selectedCountry: string): void {
    const phoneNumberControl = this.registrationForm.get('phoneNumber');

    if (
      selectedCountry === 'United States of America' ||
      selectedCountry === 'Canada'
    ) {
      phoneNumberControl?.setValidators([
        Validators.required,
        Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/),
      ]);
    } else {
      phoneNumberControl?.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]{1,12}$/),
      ]);
    }

    phoneNumberControl?.updateValueAndValidity();
    this.formatPhoneNumber({
      target: { value: phoneNumberControl?.value || '' },
    });
  }

  formatPhoneNumber(event: any): void {
    const input = event.target;
    let value = input.value.replace(/\D/g, '');

    if (value.length === 0) {
      input.value = '';
      return;
    }

    const selectedCountry = this.registrationForm.get('country')?.value;
    if (
      selectedCountry === 'United States of America' ||
      selectedCountry === 'Canada'
    ) {
      if (value.length <= 3) {
        value = `(${value}`;
      } else if (value.length <= 6) {
        value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
      } else {
        value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(
          6,
          10
        )}`;
      }
    } else {
      if (value.length > 12) {
        value = value.slice(0, 12);
      }
    }
    input.value = value;
    this.registrationForm.get('phoneNumber')?.setValue(input.value);
  }

  onFocus(): void {
    const repeatPasswordControl = this.registrationForm.get('repeatPassword');
    repeatPasswordControl?.markAsTouched();
  }

  checkPasswords(): void {
    const password = this.registrationForm.get('password')?.value;
    const repeatPassword = this.registrationForm.get('repeatPassword')?.value;

    if (password && repeatPassword && password !== repeatPassword) {
      this.registrationForm
        .get('repeatPassword')
        ?.setErrors({ passwordMismatch: true });
    } else {
      this.registrationForm.get('repeatPassword')?.setErrors(null);
    }

    if (!repeatPassword) {
      this.registrationForm
        .get('repeatPassword')
        ?.setErrors({ required: true });
    }
  }

  isPasswordValid(): boolean {
    const password = this.registrationForm.get('password')?.value;
    const repeatPassword = this.registrationForm.get('repeatPassword')?.value;

    return (
      password === repeatPassword && password !== '' && repeatPassword !== ''
    );
  }

  onSubmit(): void {
    if (this.registrationForm.valid && this.isPasswordValid()) {
      const countryCode = this.registrationForm
        .get('phoneCode')
        ?.value.replace('+', '');
      const phoneNumber = this.registrationForm
        .get('phoneNumber')
        ?.value.replace(/\D/g, '');

      const formData = {
        fullName: this.registrationForm.get('fullName')?.value,
        email: this.registrationForm.get('email')?.value,
        phoneNumber: `${countryCode}${phoneNumber}`,
        password: this.registrationForm.get('password')?.value,
      };

      this.authService.registerUser(formData).subscribe({
        next: (response) => {
          this.notificationService.showNotification(undefined, 'Registration successful!. Redirecting to login page.',
            true
          );

          setTimeout(() => {
            this.router.navigate(['/auth/login']);
          }, 4000);

          this.registrationForm.reset();
        },
        error: (error) => {
          let errorMessage = '';

          if (typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (
            error.message?.includes('Unknown Error') ||
            error.status === 0
          ) {
            errorMessage =
              'Failed to register the user. Please contact the administrator.';
          } else {
            errorMessage =
              error.error?.message ||
              'An unexpected error occurred. Please try again.';
          }

          this.notificationService.showNotification(errorMessage);
        },
      });
    } else {
      this.notificationService.showNotification(
        'Please correct the errors before submitting.'
      );
    }
  }
}
